import { useEffect, useMemo, useState } from 'react';
import { CalculatorTabs } from '@/features/application/components';
import { CreditLineCalculatorPage } from '../CreditLineCalculatorPage';
import { InstallmentCreditCalculatorPage } from '../InstallmentCreditCalculatorPage';
import styles from './CalculatorPage.module.scss';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useLoanOffer } from '@/features/offer';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { abacus } from '@/assets/images/emojis';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '@/routes/routes';
import { useApplication } from '../../hooks/useApplication';
import {
  calculateCreditLineOfferDetails,
  calculateInstallmentLoanDetails,
} from '@/features/offer/hooks/loanOfferUtils';
import { CreditProductType } from '@/features/offer/types';
import { getPreliminaryOffers } from '@/lib/offerUtils';

interface CalculatorPageProps {
  // A preliminary offer, opened from its landing for one credit (no tabs);
  // `onContinue` decides where to (no derogation or confirmation step).
  isPreliminary?: boolean;
  onContinue?: () => void;
  continueText?: string;
}

export function CalculatorPage({
  isPreliminary = false,
  onContinue,
  continueText = 'Salvează',
}: CalculatorPageProps) {
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: 'credit-line' | 'installment' }>();

  const {
    loanOfferState,
    creditLineOffer: finalCreditLineOffer,
    installmentOffer: finalInstallmentOffer,
    isLoading: isLoanOffersStateLoading,
  } = useLoanOffer(true);

  const preliminaryOffers = getPreliminaryOffers(loanOfferState?.creditOffers);
  const creditLineOffer = isPreliminary
    ? preliminaryOffers.find(
        (offer) => offer.type === CreditProductType.WhiteLabelCard
      )
    : finalCreditLineOffer;
  const installmentOffer = isPreliminary
    ? preliminaryOffers.find((offer) => offer.type === CreditProductType.Consumer)
    : finalInstallmentOffer;
  const hasCreditLineOffer = !!creditLineOffer;
  const hasInstallmentOffer = !!installmentOffer;
  const preliminaryOffer = (calculatorTab: 'credit-line' | 'installment') =>
    isPreliminary
      ? calculatorTab === 'credit-line'
        ? creditLineOffer
        : installmentOffer
      : undefined;
  const getTabRoute = isPreliminary
    ? routes.offer.preliminaryCalculator
    : routes.application.calculatorTab;
  const { installmentDraft, creditLineDraft } = useApplication();

  const installmentDetails = calculateInstallmentLoanDetails(
    installmentOffer,
    installmentDraft?.amount,
    installmentDraft?.numberOfRates
  );
  const creditLineDetails = calculateCreditLineOfferDetails(
    creditLineOffer,
    creditLineDraft
  );

  const [activeTab, setActiveTab] = useState<'credit-line' | 'installment'>(
    tab === 'installment' ? 'installment' : 'credit-line'
  );

  const handleTabChange = (nextTab: 'credit-line' | 'installment') => {
    setActiveTab(nextTab);
    navigate(getTabRoute(nextTab), { replace: false });
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }

    const isCreditLine = activeTab === 'credit-line';
    const confirmation = isCreditLine
      ? routes.application.creditLineConfirmation
      : routes.application.installmentConfirmation;
    const isDerogation = isCreditLine
      ? creditLineDetails?.isDerogation
      : installmentDetails?.isDerogation;

    if (isDerogation) {
      navigate(routes.application.derogationDeclaration, {
        state: { returnTo: confirmation },
      });
    } else {
      navigate(confirmation);
    }
  };

  const showTabs = !isPreliminary && hasCreditLineOffer && hasInstallmentOffer;

  const defaultTab: 'credit-line' | 'installment' = useMemo(() => {
    if (hasCreditLineOffer) return 'credit-line';
    return 'installment';
  }, [hasCreditLineOffer]);

  useEffect(() => {
    if (isLoanOffersStateLoading) return;

    const isValidTab = tab === 'credit-line' || tab === 'installment';
    const isOffered = (t: 'credit-line' | 'installment') =>
      (t === 'credit-line' && hasCreditLineOffer) ||
      (t === 'installment' && hasInstallmentOffer);

    // Determine target tab based on URL param and availability
    let targetTab: 'credit-line' | 'installment' = defaultTab;
    if (isValidTab && isOffered(tab!)) {
      targetTab = tab as 'credit-line' | 'installment';
    }

    // Sync state if differs
    if (targetTab !== activeTab) {
      setActiveTab(targetTab);
    }

    // If URL param is invalid or unavailable, normalize URL to targetTab
    if (!isValidTab || !isOffered(tab as 'credit-line' | 'installment')) {
      navigate(getTabRoute(targetTab), { replace: true });
    }
  }, [
    tab,
    hasCreditLineOffer,
    hasInstallmentOffer,
    isLoanOffersStateLoading,
    defaultTab,
    activeTab,
  ]);

  if (isLoanOffersStateLoading) {
    return <PageLoader />;
  }

  const singleTab = isPreliminary
    ? activeTab
    : hasCreditLineOffer
      ? 'credit-line'
      : 'installment';

  return (
    <div className={styles.calculatorPage}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          {!isPreliminary && (
            <img src={abacus} alt="Abacus" className={styles.emoji} />
          )}
          <h1 className={styles.title}>Calculator</h1>
        </div>

        {/* Tabs */}
        {showTabs && (
          <div className={styles.calculatorTabsContainer}>
            <CalculatorTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />

            <div className={styles.calculatorContent}>
              {activeTab === 'credit-line' && (
                <CreditLineCalculatorPage offer={preliminaryOffer('credit-line')} />
              )}
              {activeTab === 'installment' && (
                <InstallmentCreditCalculatorPage
                  offer={preliminaryOffer('installment')}
                />
              )}
            </div>
          </div>
        )}

        {!showTabs && singleTab === 'credit-line' && hasCreditLineOffer && (
          <div className={styles.calculatorContent}>
            <CreditLineCalculatorPage offer={preliminaryOffer('credit-line')} />
          </div>
        )}

        {!showTabs && singleTab === 'installment' && hasInstallmentOffer && (
          <div className={styles.calculatorContent}>
            <InstallmentCreditCalculatorPage
              offer={preliminaryOffer('installment')}
            />
          </div>
        )}
      </div>
      <div className={styles.bottomContainer}>
        <Button
          trackingId={
            isPreliminary ? 'preliminary_calculator_save' : 'calculator_continue'
          }
          className={styles.button}
          text={continueText}
          rightIcon={isPreliminary ? undefined : <Icon name="arrow-right" />}
          onClick={handleContinue}
        />
      </div>
    </div>
  );
}
